import React from 'react';
import {
  HospitalBed,
  UserMultiple,
  Task,
  Warning,
} from '@carbon/icons-react';
import './DashboardOverview.css';

// Srpska množina: 1 krevet, 2-4 kreveta, 5+ kreveta
const plural = (count, one, few, many) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

const StatCard = ({ icon: Icon, tone, label, value, sub, subLines, subVisual, progress, onClick }) => {
  const className = `adash-stat ${tone ? `adash-stat--${tone}` : ''} ${onClick ? 'adash-stat--clickable' : ''}`.trim();

  const content = (
    <>
      <span className="adash-stat__head">
        <span className="adash-stat__icon">
          <Icon size={20} />
        </span>
        <span className="adash-stat__label">{label}</span>
      </span>
      <span className="adash-stat__value">{value}</span>
      {subVisual ? (
        subVisual
      ) : subLines && subLines.length > 0 ? (
        <span className="adash-stat__sub adash-stat__sub--stack">
          {subLines.map((line, index) => (
            <span className="adash-stat__sub-line" key={index}>{line}</span>
          ))}
        </span>
      ) : (
        sub && <span className="adash-stat__sub">{sub}</span>
      )}
      {typeof progress === 'number' && (
        <span
          className="adash-stat__track"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label}: ${Math.round(progress)}%`}
        >
          <span
            className="adash-stat__bar"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
};

// Segmentisana traka za karticu "Zadaci danas":
// zeleno = završeno, bledo zeleno = dodeljeno (a još nezavršeno), ostatak trake = nedodeljeno.
// Visina reda trake je "1.25em" (tačno jedna linija teksta), pa blok (traka + natpis)
// zauzima ISTI prostor kao ranije dve linije teksta - veličina kartice se ne menja.
const TaskProgress = ({ completed, assigned, total }) => {
  const safeTotal = total > 0 ? total : 0;
  const completedPct = safeTotal > 0 ? Math.min(100, Math.max(0, (completed / safeTotal) * 100)) : 0;
  const assignedPct = safeTotal > 0 ? Math.min(100, Math.max(0, (assigned / safeTotal) * 100)) : 0;
  const assignedOnlyPct = Math.max(0, assignedPct - completedPct);

  return (
    <span className="adash-stat__sub adash-stat__sub--stack">
      <span
        className="adash-stat__split-row"
        role="img"
        aria-label={`${completed} završeno, ${assigned} dodeljeno od ${total}`}
      >
        <span className="adash-stat__split-track">
          <span
            className="adash-stat__split-seg adash-stat__split-seg--done"
            style={{ width: `${completedPct}%` }}
          />
          <span
            className="adash-stat__split-seg adash-stat__split-seg--assigned"
            style={{ width: `${assignedOnlyPct}%` }}
          />
        </span>
      </span>
      <span className="adash-stat__sub-line adash-stat__sub-line--fit">
        {completed} završeno · {assigned} dodeljeno
      </span>
    </span>
  );
};

/**
 * DashboardOverview - naslov PC admin dashboard-a i red sa 4 KPI kartice
 * (popunjenost, smena, zadaci, akcije).
 */
const DashboardOverview = ({ stats, onShiftClick, onActionsClick }) => {
  const {
    occupancyPercentage = 0,
    takenBeds = 0,
    totalBeds = 0,
    employeesOnShift = 0,
    areasOnShift = 0,
    totalTasks = 0,
    completedTasks = 0,
    unassignedTasks = 0,
    actionsCount = 0,
    criticalActions = 0,
    warningActions = 0,
  } = stats || {};

  // "Dodeljeno" = svi zadaci koji imaju izvršioca (ukupno - nedodeljeno).
  const assignedTasks = Math.max(0, totalTasks - unassignedTasks);

  return (
    <div className="adash-overview">
      <div className="adash-kpis">
        <StatCard
          icon={HospitalBed}
          label="Popunjenost"
          value={`${occupancyPercentage}%`}
          sub={`${takenBeds} od ${totalBeds} ${plural(totalBeds, 'krevet', 'kreveta', 'kreveta')}`}
          progress={occupancyPercentage}
        />
        <StatCard
          icon={UserMultiple}
          label="U smeni"
          value={employeesOnShift}
          sub={`U ${areasOnShift} ${plural(areasOnShift, 'oblasti', 'oblasti', 'oblasti')}`}
          onClick={onShiftClick}
        />
        <StatCard
          icon={Task}
          label="Zadaci danas"
          value={totalTasks}
          subVisual={
            <TaskProgress
              completed={completedTasks}
              assigned={assignedTasks}
              total={totalTasks}
            />
          }
        />
        <StatCard
          icon={Warning}
          tone={actionsCount > 0 ? 'alert' : undefined}
          label="Potrebne akcije"
          value={actionsCount}
          sub={
            actionsCount === 0
              ? 'Nema potrebnih akcija'
              : `${criticalActions} ${plural(criticalActions, 'najvažnija', 'najvažnije', 'najvažnijih')} · ${warningActions} ${plural(warningActions, 'važna', 'važne', 'važnih')}`
          }
          onClick={onActionsClick}
        />
      </div>
    </div>
  );
};

export default DashboardOverview;
